import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { getGame } from '../utils/storage';

// ─── Weight deviation bins (seconds, actual − expected) ───────────────────────
// The middle four bins (-0.25 through +0.5) are the "Safe" range shown under
// the histogram; everything outside it starts yellow at the edge of Safe and
// gets progressively more red the farther it is from that range, on both
// sides independently — the two sides aren't the same length in steps, so
// the ramp doesn't need to bottom out at the same shade on both ends.
const WEIGHT_BINS = [
  { label: '≤-1.5',  color: '#991b1b' },
  { label: '-1.5',   color: '#dc2626' },
  { label: '-1.25',  color: '#ef4444' },
  { label: '-1.0',   color: '#ea580c' },
  { label: '-0.75',  color: '#fb923c' },
  { label: '-0.5',   color: '#facc15' },
  { label: '-0.25',  color: '#22c55e' },
  { label: '0',      color: '#22c55e' },
  { label: '+0.25',  color: '#22c55e' },
  { label: '+0.5',   color: '#22c55e' },
  { label: '+0.75',  color: '#facc15' },
  { label: '+1.0',   color: '#fb923c' },
  { label: '+1.25',  color: '#ea580c' },
  { label: '≥+1.5',  color: '#ef4444' },
];

function getWeightBinIndex(dev: number): number {
  if (dev < -1.5) return 0;
  if (dev >= 1.5) return 13;
  return Math.min(12, Math.floor((dev + 1.5) / 0.25)) + 1;
}

// ─── Curl deviation bins (inches, actual − expected) ─────────────────────────
// Same "Safe" range treatment as the weight bins: -4" through +4" is green,
// everything outside ramps from yellow at the edge of Safe to red the
// farther out it is.
const CURL_BINS = [
  { label: '≤-16"', color: '#ea580c' },
  { label: '-12"',  color: '#fb923c' },
  { label: '-8"',   color: '#facc15' },
  { label: '-4"',   color: '#22c55e' },
  { label: '0"',    color: '#22c55e' },
  { label: '+4"',   color: '#22c55e' },
  { label: '+8"',   color: '#facc15' },
  { label: '+12"',  color: '#fb923c' },
  { label: '≥+16"', color: '#ea580c' },
];

function getCurlBinIndex(dev: number): number {
  if (dev < -12) return 0;
  if (dev >= 16) return 8;
  return Math.min(7, Math.floor((dev + 12) / 4)) + 1;
}

// ─── 8-zone classifier ────────────────────────────────────────────────────────
// Odd ends = away (throwing toward far end), even ends = home (throwing toward near end)
// Clockwise turn = curls right; rock starts aimed at broom and moves away from it
// Out→In: curl is toward the center line  |  In→Out: curl is away from center line
type ZoneKey =
  | 'away-left-left-right'  | 'away-left-right-left'
  | 'away-right-left-right' | 'away-right-right-left'
  | 'home-left-left-right'  | 'home-left-right-left'
  | 'home-right-left-right' | 'home-right-right-left';

function getShotZone(endNumber: number, broomPosition: number, turn: string): ZoneKey {
  const end       = endNumber % 2 !== 0 ? 'away' : 'home';
  const side      = broomPosition >= 0  ? 'right' : 'left';
  // Clockwise curls right → rock travels left-to-right
  // Counter-clockwise curls left → rock travels right-to-left
  const direction = turn === 'clockwise' ? 'left-right' : 'right-left';
  return `${end}-${side}-${direction}` as ZoneKey;
}

// ─── Histogram data builder ───────────────────────────────────────────────────
function lightenColor(hex: string, amount: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgb(${Math.round(r + (255 - r) * amount)},${Math.round(g + (255 - g) * amount)},${Math.round(b + (255 - b) * amount)})`;
}

interface BinEntry { dev: number; isHit: boolean }
interface CurlEntry extends BinEntry { zone: ZoneKey }

function buildWeightData(devs: BinEntry[]) {
  const draws = new Array(14).fill(0);
  const hits  = new Array(14).fill(0);
  devs.forEach(({ dev, isHit }) => { const i = getWeightBinIndex(dev); (isHit ? hits : draws)[i]++; });
  return WEIGHT_BINS.map((b, i) => ({ label: b.label, drawCount: draws[i], hitCount: hits[i], color: b.color }));
}

function buildCurlData(devs: BinEntry[]) {
  const draws = new Array(9).fill(0);
  const hits  = new Array(9).fill(0);
  devs.forEach(({ dev, isHit }) => { const i = getCurlBinIndex(dev); (isHit ? hits : draws)[i]++; });
  return CURL_BINS.map((b, i) => ({ label: b.label, drawCount: draws[i], hitCount: hits[i], color: b.color }));
}

// ─── 8-zone grid layout (mirrors a top-down sheet view) ──────────────────────
// Columns 0-1 = left side, columns 2-3 = right side
// Within each side: outer direction is at the edge, inner direction is at center
// Grid arranged symmetrically around the center line:
// Left side: [CCW (outer), CW (toward center)]
// Right side: [CCW (toward center), CW (outer)]
const ZONE_GRID: { key: ZoneKey; col: number; row: number; label: string }[] = [
  { key: 'away-left-right-left',  col: 0, row: 0, label: 'CCW' },
  { key: 'away-left-left-right',  col: 1, row: 0, label: 'CW' },
  { key: 'away-right-right-left', col: 2, row: 0, label: 'CCW' },
  { key: 'away-right-left-right', col: 3, row: 0, label: 'CW' },
  { key: 'home-left-right-left',  col: 0, row: 1, label: 'CCW' },
  { key: 'home-left-left-right',  col: 1, row: 1, label: 'CW' },
  { key: 'home-right-right-left', col: 2, row: 1, label: 'CCW' },
  { key: 'home-right-left-right', col: 3, row: 1, label: 'CW' },
];

// ─── Component ────────────────────────────────────────────────────────────────
export function PlayerSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (!gameId) {
      setIsLoading(false);
      return;
    }

    getGame(gameId)
      .then(current => {
        if (cancelled) return;
        if (current) setGame(current);
      })
      .catch(e => console.error('Error loading game:', e))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  if (isLoading) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 flex items-center justify-center">
      <p className="text-blue-300">Loading...</p>
    </div>
  );

  if (!game) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <p className="text-blue-300 mb-4">Game not found</p>
        <button onClick={() => navigate('/CurlingCodex')} className="text-blue-300 hover:text-blue-200">
          Return to Dashboard
        </button>
      </div>
    </div>
  );

  // ── Collect player stats ──────────────────────────────────────────────────
  const shotTypeToZone: Record<string, number> = {
    'high-guard': 1, 'mid-guard': 2, 'tight-guard': 3,
    'top-twelve': 4, 'top-eight': 5, 'top-four': 6, 'tee-line': 7,
    'back-four': 8, 'back-eight': 9, 'back-twelve': 10,
    'hack': 11, 'board': 12, 'control': 13, 'normal': 14, 'firm': 15, 'peel': 16,
  };

  const playerStats = new Map<string, {
    playerName: string; team: 'team1' | 'team2';
    totalShots: number;
    excellent: number; good: number; fair: number; poor: number; miss: number;
    heavyHits: number; heavyDraws: number; lightHits: number; lightDraws: number;
    goldStar: number; bomb: number;
    weightDeviations: BinEntry[];
    curlDeviations: CurlEntry[];
  }>();

  if (Array.isArray(game.ends)) {
    game.ends.forEach((end: any) => {
      if (!Array.isArray(end.shots)) return;
      end.shots.forEach((shot: any) => {
        if (!shot?.playerName || shot.skipped) return;

        if (!playerStats.has(shot.playerName)) {
          playerStats.set(shot.playerName, {
            playerName: shot.playerName, team: shot.team,
            totalShots: 0,
            excellent: 0, good: 0, fair: 0, poor: 0, miss: 0,
            heavyHits: 0, heavyDraws: 0, lightHits: 0, lightDraws: 0,
            goldStar: 0, bomb: 0,
            weightDeviations: [], curlDeviations: [],
          });
        }

        const stats = playerStats.get(shot.playerName)!;
        stats.totalShots++;
        if (shot.result) (stats as any)[shot.result]++;
        if (shot.isGoldStar) stats.goldStar++;
        if (shot.isBomb) stats.bomb++;

        const zone  = shotTypeToZone[shot.shotType] ?? 0;
        const isHit = zone >= 11;
        const isDraw = zone >= 1 && zone <= 10;

        if (shot.weight === 'heavy') { if (isHit) stats.heavyHits++; if (isDraw) stats.heavyDraws++; }
        else if (shot.weight === 'light') { if (isHit) stats.lightHits++; if (isDraw) stats.lightDraws++; }

        if (shot.time > 0 && shot.expectedTime > 0) {
          stats.weightDeviations.push({ dev: shot.time - shot.expectedTime, isHit });
        }

        if (shot.curl != null && shot.expectedCurl != null &&
            shot.turn && shot.broomPosition != null) {
          const curlZone = getShotZone(end.number, shot.broomPosition, shot.turn);
          stats.curlDeviations.push({ dev: shot.curl - shot.expectedCurl, isHit, zone: curlZone });
        }
      });
    });
  }

  const team1Players = Array.from(playerStats.values()).filter(p => p.team === 'team1');
  const team2Players = Array.from(playerStats.values()).filter(p => p.team === 'team2');

  // ── Histogram bar renderer ────────────────────────────────────────────────
  const renderBars = (
    data: { label: string; drawCount: number; hitCount: number; color: string }[],
    height = 64,
    showLegend = false,
    safeRange?: [number, number], // inclusive [startIndex, endIndex] into `data` to mark as an acceptable range
  ) => {
    const maxCount = Math.max(...data.map(d => d.drawCount + d.hitCount), 1);
    return (
      <>
        {showLegend && (
          <div className="flex items-center gap-3 mb-1">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#22c55e' }} />
              <span style={{ fontSize: 9, color: '#ffffff' }}>Dark = Draw</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: lightenColor('#22c55e', 0.55) }} />
              <span style={{ fontSize: 9, color: '#ffffff' }}>Light = Hit</span>
            </div>
          </div>
        )}
        <div className="flex" style={{ height }}>
          {/* Count scale: the max bar's value at top, 0 at the baseline, so the
              bar heights read as an actual count rather than an unlabeled shape. */}
          <div className="flex flex-col justify-between items-end pr-1 shrink-0" style={{ width: 16, fontSize: 8, color: '#ffffff' }}>
            <span>{maxCount}</span>
            <span>0</span>
          </div>
          <div className="flex items-end gap-px flex-1" style={{ height: '100%' }}>
            {data.map((bin, i) => {
              const total   = bin.drawCount + bin.hitCount;
              const hasDraw = bin.drawCount > 0;
              const hasHit  = bin.hitCount > 0;
              // Floor each populated segment at 16px (rather than scaling
              // purely by count) so there's always room to stamp its count
              // inside it — a bar's total height grows to fit both segments
              // when a bin has both draws and hits.
              const minSeg = 16;
              let barH = total > 0 ? Math.max(16, (total / maxCount) * height) : 2;
              if (hasDraw && hasHit) barH = Math.max(barH, minSeg * 2);
              let drawH = 0;
              let hitH = 0;
              if (hasDraw && hasHit) {
                const remaining = barH - 2 * minSeg;
                drawH = minSeg + Math.max(0, Math.round(remaining * (bin.drawCount / total)));
                hitH  = barH - drawH;
              } else if (hasDraw) {
                drawH = barH;
              } else if (hasHit) {
                hitH = barH;
              }
              const labelStyle = {
                fontSize: 9, fontWeight: 700, color: '#ffffff',
                textShadow: '0 0 2px rgba(0,0,0,0.9), 0 0 3px rgba(0,0,0,0.9)',
              };
              return (
                <div
                  key={i}
                  className="flex-1 flex flex-col justify-end"
                  style={{ height: '100%' }}
                  title={`${bin.label}: ${bin.drawCount} draw${bin.drawCount !== 1 ? 's' : ''}, ${bin.hitCount} hit${bin.hitCount !== 1 ? 's' : ''}`}
                >
                  {total > 0 ? (
                    <div style={{ height: barH, display: 'flex', flexDirection: 'column', borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
                      {hitH > 0 && (
                        <div style={{ height: hitH, backgroundColor: lightenColor(bin.color, 0.55), flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={labelStyle}>{bin.hitCount}</span>
                        </div>
                      )}
                      {drawH > 0 && (
                        <div style={{ height: drawH, backgroundColor: bin.color, opacity: 0.9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={labelStyle}>{bin.drawCount}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ height: 2, backgroundColor: bin.color, opacity: 0.15 }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex gap-px mt-0.5">
          <div className="shrink-0" style={{ width: 16 }} />
          {data.map((bin, i) => (
            <div key={i} className="flex-1 text-center" style={{ fontSize: 9, color: '#ffffff' }}>
              {bin.label}
            </div>
          ))}
        </div>
        {safeRange && (
          <div className="flex gap-px mt-1">
            <div className="shrink-0" style={{ width: 16 }} />
            {(() => {
              const [start, end] = safeRange;
              const cols: JSX.Element[] = [];
              for (let i = 0; i < data.length; i++) {
                if (i === start) {
                  cols.push(
                    <div key={i} style={{ flex: end - start + 1 }} className="flex flex-col items-center">
                      <div className="w-full border-t-2 border-emerald-400" />
                      <span className="mt-0.5" style={{ fontSize: 9, color: '#34d399' }}>Safe</span>
                    </div>
                  );
                  i = end;
                } else {
                  cols.push(<div key={i} style={{ flex: 1 }} />);
                }
              }
              return cols;
            })()}
          </div>
        )}
      </>
    );
  };

  // ── Curl zone map (8-zone grid, mirrors top-down sheet) ──────────────────
  const renderCurlZoneMap = (curlDeviations: CurlEntry[], totalShots: number) => {
    if (curlDeviations.length === 0) return (
      <p className="text-xs text-white italic">No curl data recorded</p>
    );

    return (
      <div>
        {/* Legend + count */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#22c55e' }} />
              <span style={{ fontSize: 9, color: '#ffffff' }}>Dark = Draw</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: lightenColor('#22c55e', 0.55) }} />
              <span style={{ fontSize: 9, color: '#ffffff' }}>Light = Hit</span>
            </div>
          </div>
          <span className="text-xs text-white">{curlDeviations.length} of {totalShots} shots with curl data</span>
        </div>

        {(['away', 'home'] as const).map(endKey => {
          const rowZones = ZONE_GRID.filter(z => z.row === (endKey === 'away' ? 0 : 1))
            .sort((a, b) => a.col - b.col);
          const isAway = endKey === 'away';
          const renderZoneBox = (zone: typeof rowZones[number]) => {
            const devs = curlDeviations.filter(d => d.zone === zone.key);
            return (
              <div key={zone.key} className="bg-white/5 rounded p-1">
                <div className="text-center mb-1" style={{ fontSize: 8, color: isAway ? '#7dd3fc' : '#86efac' }}>
                  {zone.label} <span style={{ color: '#ffffff' }}>({devs.length})</span>
                </div>
                {renderBars(buildCurlData(devs), 40, false, [3, 5])}
              </div>
            );
          };
          return (
            <div key={endKey} className={isAway ? 'mb-3' : ''}>
              <div className="flex items-center gap-1 mb-1">
                <div className="w-12 shrink-0" style={{ fontSize: 9, color: isAway ? '#7dd3fc' : '#86efac' }}>
                  {isAway ? 'Away End' : 'Home End'}
                </div>
                <div className={`flex-1 border-t ${isAway ? 'border-blue-400/30' : 'border-green-400/30'}`} />
              </div>

              {/* Bordered mini top-down layout: the away and home ends throw
                  in opposite directions down the sheet, so hog line / back
                  board swap top-to-bottom between them — but sheet left/right
                  stay fixed on the same side for both, matching the Sheet
                  Map's convention (broomPosition's sign never flips by end). */}
              <div className="border border-white/15 rounded-lg p-2">
                <div className="text-center mb-1" style={{ fontSize: 8, color: '#ffffff' }}>
                  {isAway ? 'Back board' : 'Hog line'}
                </div>
                <div className="flex items-stretch gap-1">
                  <div className="flex items-center justify-center shrink-0" style={{ width: 10 }}>
                    <span style={{ fontSize: 8, color: '#ffffff', writingMode: 'vertical-rl', whiteSpace: 'nowrap' }}>
                      Sheet Left side
                    </span>
                  </div>
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-1 items-stretch flex-1">
                    <div className="space-y-1">
                      {renderZoneBox(rowZones[0])}
                      {renderZoneBox(rowZones[1])}
                    </div>
                    <div className="flex flex-col items-center shrink-0" style={{ width: 12 }}>
                      <div className="flex-1 border-l border-dashed border-white/40" />
                      <span className="my-0.5" style={{ fontSize: 7, color: '#ffffff', writingMode: 'vertical-rl', whiteSpace: 'nowrap' }}>
                        Center Line
                      </span>
                      <div className="flex-1 border-l border-dashed border-white/40" />
                    </div>
                    <div className="space-y-1">
                      {renderZoneBox(rowZones[2])}
                      {renderZoneBox(rowZones[3])}
                    </div>
                  </div>
                  <div className="flex items-center justify-center shrink-0" style={{ width: 10 }}>
                    <span style={{ fontSize: 8, color: '#ffffff', writingMode: 'vertical-rl', whiteSpace: 'nowrap' }}>
                      Sheet Right side
                    </span>
                  </div>
                </div>
                <div className="text-center mt-1" style={{ fontSize: 8, color: '#ffffff' }}>
                  {isAway ? 'Hog line' : 'Back board'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Per-player panel ─────────────────────────────────────────────────────
  const renderPlayerPanels = (players: ReturnType<typeof Array.from<typeof playerStats extends Map<string, infer V> ? V : never>>) => {
    const withData = players.filter(p => p.weightDeviations.length > 0 || p.curlDeviations.length > 0);
    if (withData.length === 0) return null;

    return (
      <div className="mt-4 space-y-4">
        {withData.map(player => {
          const weightData = buildWeightData(player.weightDeviations);
          return (
            <div key={player.playerName} className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl p-4 space-y-5">
              <span className="text-sm font-semibold text-white">{player.playerName}</span>

              {/* Weight deviation histogram */}
              {player.weightDeviations.length > 0 && (
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-xs text-blue-300 font-medium">Weight Deviation - Over and Under Thrown (seconds)</span>
                    <span className="text-xs text-white">{player.weightDeviations.length} shots</span>
                  </div>
                  <div className="flex justify-between text-xs text-white mb-1">
                    <span>← Heavy</span><span>Light →</span>
                  </div>
                  {renderBars(weightData, 64, true, [6, 9])}
                </div>
              )}

              {/* Curl deviation zone map */}
              <div>
                <div className="mb-2">
                  <span className="text-xs text-purple-300 font-medium">Curl Deviation - Overcurl and Undercurl (inches)</span>
                </div>
                {renderCurlZoneMap(player.curlDeviations, player.totalShots)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Player table + panels ─────────────────────────────────────────────────
  const renderTeamSection = (
    players: ReturnType<typeof Array.from<typeof playerStats extends Map<string, infer V> ? V : never>>,
    teamName: string,
  ) => {
    if (players.length === 0) return null;
    return (
      <div className="mb-8">
        <h2 className="text-xl font-bold text-white mb-4">{teamName}</h2>
        <div className="overflow-x-auto">
          <table className="w-full bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 overflow-hidden">
            <thead className="bg-white/5 border-b border-white/15">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-blue-300 uppercase tracking-wider">Player</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Shots</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Accuracy</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Heavy Hits</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Heavy Draws</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Light Hits</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Light Draws</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Gold Star</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Bombs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {players.map((player, idx) => {
                const accuracy = player.totalShots > 0
                  ? ((4 * player.excellent + 3 * player.good + 2 * player.fair + player.poor) / (4 * player.totalShots)) * 100
                  : 0;
                return (
                  <tr key={player.playerName} className={`hover:bg-white/5 ${idx % 2 === 0 ? 'bg-white/5' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white">{player.playerName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">{player.totalShots}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold text-white">{accuracy.toFixed(1)}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">{player.heavyHits}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">{player.heavyDraws}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">{player.lightHits}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">{player.lightDraws}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">{player.goldStar}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">{player.bomb}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-3 mb-1">
          <p className="text-sm text-blue-300 font-medium">Shot Deviation Analysis</p>
        </div>
        {renderPlayerPanels(players)}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              // Return to whichever page the user actually came from (history or the
              // game summary) rather than guessing. Only synthesize a destination if
              // there's no in-app page to pop back to.
              if (location.key !== 'default') {
                navigate(-1);
              } else {
                navigate(`/CurlingCodex/game/${gameId}`);
              }
            }}
            className="flex items-center gap-2 text-blue-300 hover:text-blue-200"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-white">Player Summary</h1>
          <div className="w-24" />
        </div>

        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm text-blue-300 mb-1">{game.team1Name || 'Team 1'}</h3>
              <p className="text-2xl font-bold text-white">{game.team1TotalScore || 0}</p>
            </div>
            <div>
              <h3 className="text-sm text-blue-300 mb-1">{game.team2Name || 'Team 2'}</h3>
              <p className="text-2xl font-bold text-white">{game.team2TotalScore || 0}</p>
            </div>
          </div>
        </div>

        {team1Players.length === 0 && team2Players.length === 0 ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <p className="text-blue-300">No player data available yet. Record some shots to see player statistics.</p>
          </div>
        ) : (
          <>
            {renderTeamSection(team1Players, game.team1Name || 'Team 1')}
            {renderTeamSection(team2Players, game.team2Name || 'Team 2')}
          </>
        )}
      </div>
    </div>
  );
}
